import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  effect,
} from "@angular/core";
import { MatCardModule } from "@angular/material/card";
import { NgxEchartsModule } from "ngx-echarts";
import { ECharts, EChartsOption } from "echarts";
import {
  AmbientLight,
  Color,
  DirectionalLight,
  Euler,
  Group,
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  TorusGeometry,
  Vector3,
} from "three";
import { NeckOrientation } from "../../services/neck-angle.service";
import { ThemeService } from "@app/core/layout/theme.service";

@Component({
  selector: "app-measured-data-neck-angle-card",
  templateUrl: "./measured-data-neck-angle-card.component.html",
  styleUrls: ["./measured-data-neck-angle-card.component.scss"],
  imports: [MatCardModule, NgxEchartsModule],
  host: {
    "(window:resize)": "onResize()",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasuredDataNeckAngleCardComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @Input() frameless = false;
  @Input() chartOption: EChartsOption = {};
  @Input() currentChartOption: EChartsOption = {};
  @Input() currentOrientation: NeckOrientation | null = null;
  @Output() chartInit = new EventEmitter<ECharts>();
  @Output() currentChartInit = new EventEmitter<ECharts>();
  @ViewChild("orientationCanvas", { static: true })
  orientationCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild("orientationLayer", { static: true })
  orientationLayer?: ElementRef<HTMLDivElement>;

  private baseChart: ECharts | null = null;
  private overlayChart: ECharts | null = null;
  private renderer?: WebGLRenderer;
  private scene?: Scene;
  private camera?: PerspectiveCamera;
  private modelGroup?: Group;
  private bandMaterial?: MeshStandardMaterial;
  private frameId?: number;
  protected chartsReady = false;
  private readonly hostEl = inject(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly themeService = inject(ThemeService);

  // テーマ変更に追従してモデルの色・不透明度を更新する。
  // ダークモードでは背景とのコントラスト不足で見えなくなるため、
  // 明るめの色＋高めの不透明度に切り替える。
  private readonly themeEffect = effect(() => {
    this.themeService.effectiveTheme();
    this.applyThemeColors();
  });
  private resizeObserver?: ResizeObserver;
  private chartBootstrapFrameId?: number;
  private destroyed = false;
  protected readonly noDataLabel = $localize`:@@home.neckAngleCard.noData:姿勢データなし`;
  protected readonly angleInfoLabel = $localize`:@@home.neckAngleCard.title.angleInfo:角度情報`;

  protected get hasChartOption(): boolean {
    const option = this.chartOption as Record<string, unknown> | null | undefined;
    return !!option && Object.keys(option).length > 0;
  }

  onBaseChartInit(chart: ECharts): void {
    if (this.destroyed) {
      return;
    }
    this.baseChart = chart;
    if (!this.destroyed) {
      this.chartInit.emit(chart);
    }
    this.resizeCharts();
    requestAnimationFrame(() => {
      if (!this.destroyed) {
        this.resizeCharts();
        this.cdr.markForCheck();
      }
    });
  }

  onCurrentChartInit(chart: ECharts): void {
    if (this.destroyed) {
      return;
    }
    this.overlayChart = chart;
    if (!this.destroyed) {
      this.currentChartInit.emit(chart);
    }
    this.resizeCharts();
    requestAnimationFrame(() => {
      if (!this.destroyed) {
        this.resizeCharts();
        this.cdr.markForCheck();
      }
    });
  }

  onResize(): void {
    this.ensureChartsReady();
    this.resizeCharts();
  }

  private resizeCharts(): void {
    if (!this.chartsReady || !this.hasRenderableHostSize()) {
      return;
    }
    this.baseChart?.resize();
    this.overlayChart?.resize();
    this.updateOverlayPosition();
  }

  ngAfterViewInit(): void {
    this.initScene();
    this.startRenderLoop();
    this.ensureChartsReady();
    this.resizeObserver = new ResizeObserver(() => {
      this.ensureChartsReady();
      this.resizeCharts();
    });
    this.resizeObserver.observe(this.hostEl.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["currentOrientation"]) {
      this.applyOrientation(this.currentOrientation);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    if (this.chartBootstrapFrameId) {
      cancelAnimationFrame(this.chartBootstrapFrameId);
      this.chartBootstrapFrameId = undefined;
    }
    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
    }
    try {
      if (this.baseChart && !this.baseChart.isDisposed()) {
        this.baseChart.dispose();
      }
    } catch {
      // ignore
    }
    try {
      if (this.overlayChart && !this.overlayChart.isDisposed()) {
        this.overlayChart.dispose();
      }
    } catch {
      // ignore
    }
    this.baseChart = null;
    this.overlayChart = null;
    // Three.js リソースを明示的に解放して WebGL コンテキストリークを防止
    if (this.modelGroup) {
      this.modelGroup.traverse((obj) => {
        if ((obj as Mesh).isMesh) {
          const mesh = obj as Mesh;
          mesh.geometry?.dispose();
          const mat = mesh.material;
          if (Array.isArray(mat)) {
            mat.forEach((m) => m.dispose());
          } else {
            (mat as MeshStandardMaterial)?.dispose();
          }
        }
      });
    }
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
    this.renderer = undefined;
    this.scene = undefined;
    this.camera = undefined;
    this.modelGroup = undefined;
    this.bandMaterial = undefined;
  }

  private hasRenderableHostSize(): boolean {
    const host = this.hostEl.nativeElement as HTMLElement;
    return host.clientWidth > 0 && host.clientHeight > 0;
  }

  private ensureChartsReady(): void {
    if (this.destroyed || this.chartsReady) {
      return;
    }
    if (this.hasRenderableHostSize()) {
      this.chartsReady = true;
      this.cdr.markForCheck();
      return;
    }
    if (this.chartBootstrapFrameId) {
      return;
    }
    this.chartBootstrapFrameId = requestAnimationFrame(() => {
      this.chartBootstrapFrameId = undefined;
      if (this.destroyed || this.chartsReady) {
        return;
      }
      if (this.hasRenderableHostSize()) {
        this.chartsReady = true;
        this.cdr.markForCheck();
      }
    });
  }

  private initScene(): void {
    const canvas = this.orientationCanvas?.nativeElement;
    if (!canvas) {
      return;
    }

    const scene = new Scene();
    const camera = new PerspectiveCamera(35, 1, 0.1, 300);
    camera.position.set(0, 0, 60);

    const renderer = new WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;

    // Lights
    const ambient = new AmbientLight(0xffffff, 0.6);
    const directional = new DirectionalLight(0xffffff, 0.4);
    directional.position.set(5, 10, 8);
    scene.add(ambient, directional);

    // 軸表示（X=赤, Y=緑, Z=青）
    // const axes = new AxesHelper(2.5);
    // scene.add(axes);

    // Neckband-like shape (subtle, low opacity)
    // 色・不透明度は applyThemeColors() でテーマに応じて設定する
    const material = new MeshStandardMaterial({
      color: new Color("#6b8093"),
      transparent: true,
      opacity: 0.12,
      roughness: 0.9,
      metalness: 0,
    });
    this.bandMaterial = material;
    this.applyThemeColors();

    const radius = 1.0;
    const arcLength = Math.PI * 1.5; // 約270度
    const bandGeometry = new TorusGeometry(radius, 0.26, 28, 140, arcLength);
    const bandMesh = new Mesh(bandGeometry, material);
    // XZ平面（Y=0）に寝かせ、正面が +Z 側に開くよう回転
    bandMesh.rotation.set(-Math.PI / 2, 0, -Math.PI / 4);
    // 回転後の閉じ側（角度0）の位置を逆ベクトルでオフセットし、原点に合わせる
    bandMesh.position.set(0, 0, radius);

    const group = new Group();
    group.add(bandMesh);

    // グループは回転させず、子メッシュの回転で姿勢を決める
    group.rotation.set(0, 0, 0);
    const groupScale = 6; // enlarge for visibility
    group.scale.set(groupScale, groupScale, groupScale);
    // 現時点では位置合わせなしで原点に配置
    group.position.set(0, 0, 0);
    scene.add(group);
    this.modelGroup = group;

    this.applyOrientation(this.currentOrientation, false);
    this.resizeRendererToLayer();
  }

  private startRenderLoop(): void {
    const render = () => {
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
      this.frameId = requestAnimationFrame(render);
    };
    render();
  }

  private resizeRendererToLayer(): void {
    if (!this.renderer || !this.camera || !this.orientationLayer) {
      return;
    }
    const width = this.orientationLayer.nativeElement.clientWidth || 1;
    const height = this.orientationLayer.nativeElement.clientHeight || 1;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  // テーマに応じてバンドモデルの色・不透明度を切り替える。
  // ダークモードでは背景が暗くコントラストが不足するため、
  // 明るめのグレー＋高めの不透明度で視認性を確保する。
  private applyThemeColors(): void {
    if (!this.bandMaterial) {
      return;
    }
    const isDark = this.themeService.effectiveTheme() === "dark";
    this.bandMaterial.color.set(isDark ? "#d7e2ee" : "#6b8093");
    this.bandMaterial.opacity = isDark ? 0.55 : 0.12;
    // ダークモードでは光の当たり方に依存せず見えるよう自発光を加える
    this.bandMaterial.emissive.set(isDark ? "#7d93a6" : "#000000");
    this.bandMaterial.emissiveIntensity = isDark ? 0.6 : 0;
    this.bandMaterial.needsUpdate = true;
  }

  private applyOrientation(
    orientation: NeckOrientation | null,
    renderNow: boolean = true,
  ): void {
    if (!this.modelGroup) {
      return;
    }
    const toRad = Math.PI / 180;
    // 外部カメラ視点（正面）に合わせるため roll/pitch を反転
    const yaw = (orientation?.yaw ?? 0) * toRad;
    const roll = -(orientation?.roll ?? 0) * toRad;
    const pitch = -(orientation?.pitch ?? 0) * toRad;
    // Y( yaw ) → X( pitch ) → Z( roll )
    const euler = new Euler(pitch, yaw, roll, "YXZ");
    this.modelGroup.setRotationFromEuler(euler);
    if (renderNow && this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  private updateOverlayPosition(): void {
    if (!this.baseChart || !this.orientationLayer) {
      return;
    }

    const chartDom = (this.baseChart as any).getDom?.() as
      | HTMLElement
      | undefined;
    const layerEl = this.orientationLayer.nativeElement;
    if (!chartDom) {
      return;
    }

    const contentEl = chartDom.closest(
      "mat-card-content",
    ) as HTMLElement | null;
    if (!contentEl) {
      return;
    }

    const chartRect = chartDom.getBoundingClientRect();
    const contentRect = contentEl.getBoundingClientRect();
    const offsetLeft = chartRect.left - contentRect.left;
    const offsetTop = chartRect.top - contentRect.top;

    const model = (this.baseChart as any).getModel?.();
    const xAxisModel = model?.getComponent?.("xAxis", 0);
    const yAxisModel = model?.getComponent?.("yAxis", 0);
    if (!xAxisModel || !yAxisModel) {
      return;
    }
    const [xMin, xMax] = xAxisModel.axis?.scale?.getExtent?.() ?? [0, 1];
    const [yMin, yMax] = yAxisModel.axis?.scale?.getExtent?.() ?? [0, 1];

    const topLeft = this.baseChart.convertToPixel(
      { xAxisIndex: 0, yAxisIndex: 0 },
      [xMin, yMax],
    ) as [number, number];
    const bottomRight = this.baseChart.convertToPixel(
      { xAxisIndex: 0, yAxisIndex: 0 },
      [xMax, yMin],
    ) as [number, number];
    if (!topLeft || !bottomRight) {
      return;
    }

    const width = Math.abs(bottomRight[0] - topLeft[0]);
    const height = Math.abs(bottomRight[1] - topLeft[1]);
    const left = offsetLeft + Math.min(topLeft[0], bottomRight[0]);
    const top = offsetTop + Math.min(topLeft[1], bottomRight[1]);

    layerEl.style.left = `${left}px`;
    layerEl.style.top = `${top}px`;
    layerEl.style.width = `${width}px`;
    layerEl.style.height = `${height}px`;

    // グラフ原点(0,0)のピクセル位置を取得し、キャンバスの中心に合わせるように移動
    const originPixel = this.baseChart.convertToPixel(
      { xAxisIndex: 0, yAxisIndex: 0 },
      [0, 0],
    ) as [number, number];
    if (originPixel && this.orientationCanvas) {
      const originX = originPixel[0] - Math.min(topLeft[0], bottomRight[0]);
      const originY = originPixel[1] - Math.min(topLeft[1], bottomRight[1]);
      const dx = originX - width / 2;
      const dy = originY - height / 2;
      // グリッド余白(left:58, top:20, right:16, bottom:48)によりデータ原点が
      // カード中央より右・上にずれるため、下・左方向に補正する
      const nudgeX = -10;
      const nudgeY = 12;
      this.orientationCanvas.nativeElement.style.transform = `translate(${dx + nudgeX}px, ${dy + nudgeY}px)`;
    }

    this.resizeRendererToLayer();
  }
}
